import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, RiskAssesmentFrequency } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { infiniteListDetailsViewPageSize, infiniteListPageSize } from "source/models/common/webConsts";
import { AppUtils } from "source/framework/appUtils";
import * as riskService from "source/models/services/riskService";
import { ISessionState } from "source/models/session/sessionState";
import { IListUsingComponentState, List } from "source/components/framework/List/List";
import { SaveDataObj } from "source/framework/saveData";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { useEffect, useState } from "react";
import { IRiskAssessmentListItem } from "ui/Scripts/source/models/riskAssessment/riskAssessmentListItem";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { Alert, Box } from "@mui/material";
import { RiskEditDialogAssessmentListAssessorCol } from "./RiskEditDialogAssessmentListAssessorCol";
import { RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { RiskListLevelChip } from "../RiskList/RiskListLevelChip";
import { RiskListTraceabilityChip } from "../RiskList/RiskListTraceabilityChip";
import { RiskAssessmentEditDialog } from "../RiskAssessmentEditDialog";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";

interface IRiskEditDialogAssessmentListProp {
    sessionState: ISessionState;
    companyId: string;
    dataId?: string;
    riskId: string;
    unitId: string;
    assesmentFrequency: RiskAssesmentFrequency;
    code: string;
    title: string;
    description: string;
    threat: string;
    controls: string;
    isReadOnly: boolean;
    isNew: boolean;
    detailsViewMode?: boolean;
    onAssessmentSaved?: (assessmentId: string) => void;
    onIsLoadingChange?: (isLoading: boolean) => void;
}

interface IRiskEditDialogAssessmentListState extends IListUsingComponentState<IRiskAssessmentListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    checkedIds: string[];
}

export const RiskEditDialogAssessmentList = (props: IRiskEditDialogAssessmentListProp) => {
    const [state, setState] = useState<IRiskEditDialogAssessmentListState>({
        intialized: false,
        isLoading: false,
        page: 1,
        hasMore: true,
        filter: "",
        sortColumn: "date",
        sortOrderIsAsc: false,
        items: [],
        selectedId: null,
        checkedIds: [],
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
        detailsItem: null
    });
    
    useEffect(() => {
        if (!props.onIsLoadingChange) return;
        props.onIsLoadingChange(state.isLoading);
    }, [state.isLoading]);

    const searchItems = async (page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const isNewRisk = props.riskId === Base.emptyGuid;
        let resultPage = 1;
        let resultHasMore = false;
        let items = {
            items: [] as IRiskAssessmentListItem[],
            selectedId: "",
            checkedIds: []
        };
        if (!isNewRisk) {
            const pageSize = !props.detailsViewMode ? infiniteListPageSize : infiniteListDetailsViewPageSize;
            setState((old) => ({ ...old, isLoading: true }));
            const result = await AppUtils.callService(() => riskService.getRiskAssessmentListItems(props.companyId, props.riskId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc), false, true, () => setState((old) => ({ ...old, isLoading: false })));
            if (!result) return;
            items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
            resultPage = result.page;
            resultHasMore = result.hasMore;
        }
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        setState((old) => ({
            ...old,
            intialized: true,
            page: !refreshList ? resultPage : page,
            hasMore: resultHasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || resultHasMore) : state.editDialogHasNext,
        }));
    };

    const refreshList = async (setEditDialogData: boolean = true) => {
        await searchItems(state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEditDialogData);
    };

    useEffect(() => {
        if (!props.companyId || !props.riskId) return;
        searchItems(state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }, [props.companyId, props.riskId, props.dataId ?? ""]);

    const changeFilter = (filter: string) => {
        searchItems(1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        searchItems(!nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState((old) => ({
            ...old,
            selectedId,
            checkedIds
        }));
    };

    const handleAdd = () => {
        setState((old) => ({
            ...old,
            ...List.getShowEditDialogStateChange(state, Base.emptyGuid)
        }));
    };

    const handleOpenLine = (id: string) => {
        setState((old) => ({
            ...old,
            ...List.getShowEditDialogStateChange(state, id)
        }));
    };

    const handleEditDialogSaved = (_id: string) => {
        refreshList(false);
        if (!props.onAssessmentSaved) return;
        props.onAssessmentSaved(_id);
    };

    const handleEditDialogClose = () => {
        setState((old) => ({
            ...old,
            showEditDialog: false
        }));
        refreshList(false);
    };

    const handleEditDialogPrint = () => {
        AppUtils.showErrorMessage(Translations.NotImplementedYet);
        // const item = state.items.find(i => i.id === state.editDialogId);
        // if (!item) return;
        // AppUtils.printUrl(AppUtils.getServiceUrl(`/reports/riskassessment/${item.id}`));
    };

    const getRemoveSaveData = (props: IRiskEditDialogAssessmentListProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", props.riskId);
        result.append("ids", ids);
        return result;
    };

    const remove = async (ids: string[]): Promise<ApiSuccess> => {
        if (ids.length < 1) return;
        const saveData = getRemoveSaveData(props, ids);
        // Call server
        const result = await AppUtils.callService(() => riskService.removeRiskAssessments(saveData.obj));
        if (!result) return null;
        refreshList();
        return result;
    };

    const handleRemove = async () => {
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedRiskAssessments
            : String.format(Translations.DoYouReallyWantToRemoveRiskAssessmentParameter, `${Base.timeToDateStr(selectedItems[0].date)}: ${selectedItems[0].assessor}`), false) === ConfirmationDialogResult.Yes) {
            // Call server
            remove(selectedIds);
        }
    };
    
    const setEditDialogItemByIndex = (index: number) => {
        setState((old) => ({
            ...old,
            ...List.getEditDialogItemByIndexStateChange(state, index, () => searchItems(state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true))
        }));
    };

    return (
        <>
            <VerticalContainer
                before={<>
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={changeFilter}
                                // appendElements={[
                                //     <FormControlLabel
                                //         key="showArchived"
                                //         label={Translations.ShowArchived}
                                //         control={
                                //             <Checkbox
                                //                 checked={state.showArchived}
                                //                 onChange={(_e, checked) => this.changeShowArchived(checked)}
                                //             />
                                //         }
                                //     />
                                // ]}
                            />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddRiskAssessment}
                                removeTooltip={Translations.RemoveRiskAssessment}
                                addDisabled={props.isNew || props.isReadOnly}
                                removeDisabled={props.isReadOnly || !state.selectedId && state.checkedIds.length < 1}
                                onAdd={handleAdd}
                                onRemove={handleRemove}
                            />
                        }
                    />
                    {props.isNew &&
                        <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.NewRiskMustBeSavedBeforeRiskAssessmentsCanBeAdded}</Alert>
                    }
                </>}
                main={<>
                    <List
                        intialized={true}
                        isLoading={!props.onIsLoadingChange && state.isLoading}
                        hasMore={!props.detailsViewMode && state.hasMore}
                        height={!props.detailsViewMode
                            ? "100%"
                            : "auto"
                        }
                        globalLoadingInUse={false}
                        header={{
                            columns: [
                                { size: !props.detailsViewMode ? 1 : 2, column: "date", content: Translations.Date },
                                { size: !props.detailsViewMode ? 2 : 3, column: "assessor", content: Translations.Assessor },
                                !props.detailsViewMode ? { size: 1, column: "nextAssesmentDate", content: Translations.NextAssessmentDate } : null,
                                !props.detailsViewMode ? { size: 1, column: "likelihood", content: Translations.Likelihood } : null,
                                !props.detailsViewMode ? { size: 1, column: "impact", content: Translations.Impact } : null,
                                { size: !props.detailsViewMode ? 1 : 2, column: "riskLevel", content: Translations.RiskLevel },
                                { size: !props.detailsViewMode ? 1 : 1, column: "treatmentEfficiency", content: Translations.EfficiencyPercentage },
                                !props.detailsViewMode ? { size: 2, column: "handlers", content: Translations.PersonsInCharge } : null,
                                { size: !props.detailsViewMode ? 1 : 2, column: "residualRisk", content: Translations.ResidualRisk },
                                { size: !props.detailsViewMode ? 1 : 2, column: "traceability", content: Translations.Traceability },
                            ]
                        }}
                        lines={
                            state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: !props.detailsViewMode ? 1 : 2, content: <ListLinkValue onClick={() => handleOpenLine(item.id)}>{Base.timeToDateStr(item.date)}</ListLinkValue> },
                                        { size: !props.detailsViewMode ? 2 : 3, content: <RiskEditDialogAssessmentListAssessorCol isReadOnly={props.isReadOnly} item={item} /> },
                                        !props.detailsViewMode ? { size: 1, content: Base.timeToDateStr(item.nextAssessmentDate) } : null,
                                        !props.detailsViewMode ? { size: 1, content: RiskListItem.getRiskLikelihoodText(item.likelihood) } : null,
                                        !props.detailsViewMode ? { size: 1, content: RiskListItem.getRiskImpactText(item.impact) } : null,
                                        { size: !props.detailsViewMode ? 1 : 2, content: <RiskListLevelChip riskLevel={item.riskLevel} /> },
                                        { size: !props.detailsViewMode ? 1 : 1, content: item.controlEffiency > 0 ? `${item.controlEffiency}%` : "" },
                                        !props.detailsViewMode
                                            ? {
                                                size: 2,
                                                content: <Box>
                                                    {item.handlers.map(h => {
                                                        return (
                                                            <Box key={h.code} sx={{ display: "flex", alignItems: "center" }}>
                                                                <ListStateIconSuccess
                                                                    color={h.date ? "success" : "error"}
                                                                    tooltip={h.date ? Translations.Approved : Translations.NotApproved}
                                                                />
                                                                <Box sx={{ marginLeft: ".5rem" }}>{h.name}</Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            }
                                            : null,
                                        { size: !props.detailsViewMode ? 1 : 2, content: <RiskListLevelChip riskLevel={item.residualRiskLevel} /> },
                                        { size: !props.detailsViewMode ? 1 : 2, content: <RiskListTraceabilityChip riskLevel={item.residualRiskLevel} /> }
                                    ],
                                    id: item.id
                                };
                            })
                        }
                        noDataMessage={Translations.AddRiskAssessments}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={handleSetSelectedIds}
                        onSearchLines={handleSearchLines}
                        onOpenLine={handleOpenLine}
                    />
                </>}
                disableMainInitialHeight={props.detailsViewMode}
            />
            {(state.showEditDialog && !!state.editDialogId) &&
                <RiskAssessmentEditDialog
                    sessionState={props.sessionState}
                    companyId={props.companyId}
                    riskId={props.riskId}
                    unitId={props.unitId}
                    riskAssessmentId={state.editDialogId}
                    assesmentFrequency={props.assesmentFrequency}
                    code={props.code}
                    title={props.title}
                    description={props.description}
                    threat={props.threat}
                    controls={props.controls}
                    isReadOnly={props.isNew || props.isReadOnly}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogSaved}
                    onClose={handleEditDialogClose}
                    onPrint={handleEditDialogPrint}
                    onRefreshList={() => refreshList(false)}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                />
            }
        </>
    );
};